/**
 * Session 30.41 — App update card.
 *
 * Replaces the tiny "Update available · Install" toast with a proper,
 * on-brand update panel (bottom sheet on phones, corner card on larger
 * screens) that walks the operator through the whole flow:
 *
 *   available → downloading (progress bar) → ready (installer opened)
 *             → needs-permission (one-time "allow from this source")
 *             → browser (fallback) / error (retry)
 *
 * The parent owns the state machine (see runApkInstallWithCard in App.js);
 * this component is purely presentational.
 */
import * as React from "react";
import { ArrowDownToLine, CheckCircle2, ExternalLink, RefreshCw, ShieldCheck, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const mb = (n) => `${(Number(n || 0) / (1024 * 1024)).toFixed(1)} MB`;

export function AppUpdateCard({ state, onInstall, onLater, onOpenSettings, onRetry, onDismiss }) {
  if (!state || !state.meta) return null;
  const { meta, phase, percent, received, total, error, installedVersion } = state;
  const version = meta.version_name ? `v${meta.version_name}` : "New version";
  const notes = String(meta.release_notes || "").trim();

  const heading = {
    available: "Update available",
    downloading: `Downloading ${version}…`,
    ready: "Ready to install",
    permission: "One-time permission needed",
    browser: "Downloading in your browser",
    error: "Couldn't start the update",
  }[phase] || "Update";

  const Icon = phase === "ready" ? CheckCircle2 : phase === "permission" ? ShieldCheck : phase === "error" ? RefreshCw : Smartphone;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[300] p-3 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-[400px] sm:p-0"
      role="dialog"
      aria-live="polite"
      aria-label={heading}
      data-testid="app-update-card"
    >
      <div className="overflow-hidden rounded-3xl border border-border bg-card/95 text-card-foreground shadow-2xl backdrop-blur-xl">
        <div className="h-1 w-full bg-[hsl(var(--primary))]" />
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">NFC Solutions</p>
              <h3 className="text-base font-semibold leading-tight" data-testid="app-update-heading">{heading}</h3>
              <p className="mt-0.5 text-sm text-muted-foreground" data-testid="app-update-version">
                {installedVersion ? `v${installedVersion} → ` : ""}<span className="font-medium text-foreground">{version}</span>
                {meta.size_bytes ? ` · ${mb(meta.size_bytes)}` : ""}
              </p>
            </div>
            {phase !== "downloading" ? (
              <button
                type="button"
                onClick={onLater}
                className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Dismiss"
                data-testid="app-update-dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {phase === "available" && notes ? (
            <p className="mt-3 line-clamp-3 rounded-xl bg-muted/60 p-3 text-sm text-muted-foreground" data-testid="app-update-notes">{notes}</p>
          ) : null}

          {phase === "downloading" ? (
            <div className="mt-4 space-y-2" data-testid="app-update-progress">
              <Progress value={Number.isFinite(percent) && percent >= 0 ? percent : undefined} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {Number.isFinite(percent) && percent >= 0 ? `${percent}% · ${mb(received)} of ${mb(total)}` : `${mb(received)} downloaded`} — keep the app open
              </p>
            </div>
          ) : null}

          {phase === "ready" ? (
            <p className="mt-3 text-sm text-muted-foreground">Tap <span className="font-medium text-foreground">Install</span> on the Android prompt. The app reopens on the new version — your local data is kept.</p>
          ) : null}

          {phase === "permission" ? (
            <p className="mt-3 text-sm text-muted-foreground">Android asks once to allow updates from NFC Solutions. Switch on <span className="font-medium text-foreground">"Allow from this source"</span> in the screen that opens, then come back and tap Try again.</p>
          ) : null}

          {phase === "browser" ? (
            <p className="mt-3 text-sm text-muted-foreground">Open the download notification and tap Install — your local data is kept.</p>
          ) : null}

          {phase === "error" ? (
            <p className="mt-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive" data-testid="app-update-error">{error || "Please try again."}</p>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {phase === "available" ? (
              <>
                <Button variant="ghost" className="rounded-full" onClick={onLater} data-testid="app-update-later">Later</Button>
                <Button className="rounded-full" onClick={onInstall} data-testid="app-update-install">
                  <ArrowDownToLine className="mr-1.5 h-4 w-4" /> Install now
                </Button>
              </>
            ) : null}
            {phase === "permission" ? (
              <>
                <Button variant="outline" className="rounded-full" onClick={onOpenSettings} data-testid="app-update-open-settings">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> Open settings
                </Button>
                <Button className="rounded-full" onClick={onRetry} data-testid="app-update-retry">Try again</Button>
              </>
            ) : null}
            {phase === "error" ? (
              <>
                <Button variant="ghost" className="rounded-full" onClick={onLater}>Later</Button>
                <Button className="rounded-full" onClick={onRetry} data-testid="app-update-retry"><RefreshCw className="mr-1.5 h-4 w-4" /> Try again</Button>
              </>
            ) : null}
            {phase === "ready" || phase === "browser" ? (
              <>
                <Button variant="ghost" className="rounded-full" onClick={onRetry} data-testid="app-update-reopen">Didn't see it? Retry</Button>
                <Button className="rounded-full" onClick={onDismiss} data-testid="app-update-done">Done</Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppUpdateCard;
